import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcArrowUp, IcPlus, IcTrash, IcPdf, IcCheckCircle } from "../../Icons";
import axios from "axios";
import { Button, Table, Input, Label } from "reactstrap";
import Header from "../../layout/Header/Header";
import Swal from "sweetalert2";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter, pdfTableStyles } from "../../../pdfUtils";

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(v) || 0);

function ListadoSalidas() {
  const { user } = useContext(AuthContext);
  const URL_SALIDAS = process.env.REACT_APP_URL_SALIDAS;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  const [salidas, setSalidas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [fechaFin, setFechaFin] = useState(moment().format("YYYY-MM-DD"));
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const cargar = () => {
    axios
      .post(`${URL_SALIDAS}/fechas`,
        { selectedFechaInicio: fechaInicio, selectedFechaFin: fechaFin }, auth)
      .then((response) => setSalidas(response.data))
      .catch(() => {});
  };

  useEffect(() => { cargar(); }, [fechaInicio, fechaFin]); // eslint-disable-line

  // Al abrir, la vista se para en el último mes CON movimiento en lugar del mes en curso,
  // para no arrancar en blanco cuando se captura con rezago o el día 1 del mes.
  useEffect(() => {
    axios
      .post(`${URL_SALIDAS}/fechas`, {
        selectedFechaInicio: moment().subtract(12, "months").startOf("month").format("YYYY-MM-DD"),
        selectedFechaFin: moment().format("YYYY-MM-DD"),
      }, auth)
      .then((response) => {
        const ultima = response.data.map((a) => a.fecha).sort().pop();
        if (ultima && ultima < fechaInicio) {
          setFechaInicio(moment(ultima).startOf("month").format("YYYY-MM-DD"));
          setFechaFin(moment(ultima).endOf("month").format("YYYY-MM-DD"));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  function cambiaStatus(a) {
    const nuevo = a.status === "Pendiente" ? "Cobrado" : "Pendiente";
    axios
      .patch(`${URL_SALIDAS}/${a._id}`, { status: nuevo }, auth)
      .then(() => {
        axios.post(URL_LOGS, {
          tipo: "Cambio Status Salida", detalle: `${a.idSalida} ${nuevo}`, user: user.id,
        }, auth);
        cargar();
      })
      .catch(() => Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" }));
  }

  function eliminar(a) {
    Swal.fire({
      title: "¿Dar de baja la salida?",
      text: "Se regresa el monto al saldo del cliente",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, dar de baja",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .delete(`${URL_SALIDAS}/${a._id}`, auth)
        .then(() => {
          axios.post(URL_LOGS, { tipo: "Baja Salida", detalle: `${a.idSalida}`, user: user.id }, auth);
          Swal.fire("¡Listo!", "Salida dada de baja", "success");
          cargar();
        })
        .catch(() => Swal.fire({ icon: "error", title: "Oops...", text: "Something went wrong!" }));
    });
  }

  const filtradas = salidas
    .filter((a) => filtroCliente === "" ||
      (a.clientes?.[0]?.nombre_comercial || "").toLowerCase().includes(filtroCliente.toLowerCase()))
    .filter((a) => filtroStatus === "" || a.status === filtroStatus);

  const total = filtradas.reduce((s, a) => s + (a.monto || 0), 0);

  function generaPDF() {
    const doc = new jsPDF();
    const startY = pdfHeader(doc, "Listado de Salidas", `${fechaInicio} al ${fechaFin}`);

    doc.autoTable({
      startY,
      head: [["#", "Fecha", "Cliente", "Tipo", "Monto", "Estatus"]],
      body: filtradas.map((a) => [
        a.idSalida,
        a.fecha,
        a.clientes?.[0]?.nombre_comercial || "",
        a.tipo,
        fmt(a.monto),
        a.status,
      ]),
      foot: [["", "", "", "Total", fmt(total), ""]],
      ...pdfTableStyles(),
    });

    pdfFooter(doc);
    doc.save(`Salidas_${fechaInicio}_${fechaFin}.pdf`);
  }

  return (
    <>
      <Header />
      {user.menu_salidas ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcArrowUp style={{ marginRight: 8, color: "#dc2626" }} />
                Salidas
              </h3>
              <p className="page-subtitle">Entregas al cliente, bajan su saldo</p>
            </div>
            <div className="page-actions">
              <Button color="danger" size="sm" onClick={generaPDF}>
                <IcPdf style={{ marginRight: 6 }} />
                PDF
              </Button>{" "}
              <Button color="success" size="sm" href="/SalidasCreate">
                <IcPlus style={{ marginRight: 6 }} />
                Nueva Salida
              </Button>
            </div>
          </div>

          <div className="filter-bar">
            <div>
              <Label>Fecha inicio</Label>
              <Input bsSize="sm" type="date" value={fechaInicio}
                     onChange={(e) => setFechaInicio(e.target.value)} />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input bsSize="sm" type="date" value={fechaFin}
                     onChange={(e) => setFechaFin(e.target.value)} />
            </div>
            <div>
              <Label>Cliente</Label>
              <Input bsSize="sm" type="text" placeholder="Buscar..." value={filtroCliente}
                     onChange={(e) => setFiltroCliente(e.target.value)} />
            </div>
            <div>
              <Label>Estatus</Label>
              <Input bsSize="sm" type="select" value={filtroStatus}
                     onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Cobrado">Cobrado</option>
              </Input>
            </div>
          </div>

          <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
            <thead>
              <tr>
                <th>#</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Cuenta</th>
                <th style={{ textAlign: "right" }}>Monto</th>
                <th>Estatus</th>
                <th>Baja</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((a) => (
                <tr key={a._id}>
                  <td>{a.idSalida}</td>
                  <td>{a.fecha}</td>
                  <td>{a.clientes?.[0]?.nombre_comercial}</td>
                  <td>{a.tipo}</td>
                  <td>{a.cuentas?.[0]?.name}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(a.monto)}</td>
                  <td>
                    <Button
                      size="sm"
                      color={a.status === "Cobrado" ? "success" : "warning"}
                      onClick={() => cambiaStatus(a)}
                      title="Cambiar estatus"
                    >
                      <IcCheckCircle style={{ marginRight: 4 }} />
                      {a.status}
                    </Button>
                  </td>
                  <td>
                    <Button color="danger" size="sm" onClick={() => eliminar(a)}>
                      <IcTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>Total</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoSalidas;
